/* =====================================================================
 * DMPage — DM 메인 페이지 (에이전트 DM + 팀 DM 통합)
 *
 * 좌: 에이전트 + 팀 연락처 목록 | 우: 채팅 스레드
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MessageCircle, Menu } from 'lucide-react';
import { useDMStore } from './hooks/useDMStore';
import { useDMConversationStore } from '@auw/stores';
import DMAgentList from './DMAgentList';
import DMChatThread from './DMChatThread';
import DMUserChatThread from './DMUserChatThread';
import DMSettingsModal from './DMSettingsModal';
import { onAppEvent } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { useFeature } from '@auw/feature-flags';
import type { Conversation } from '@auw/workspace-utils/types/dm';

const DMPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { activeAgent, loadUnreadCounts } = useDMStore();
  const { activeConversation, conversations } = useDMConversationStore();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const pageSidebarOpen = useWorkspaceNavigationStore((s) => s.isPageSidebarOpen('message'));
  const togglePageSidebar = useWorkspaceNavigationStore((s) => () => s.togglePageSidebar('message'));

  // 초기 로드
  useEffect(() => {
    loadUnreadCounts();
  }, [loadUnreadCounts]);

  useEffect(() => {
    return onAppEvent('workspace:togglePageSidebar', () => {
      togglePageSidebar();
    });
  }, [togglePageSidebar]);

  // 활성 대화 객체
  const activeConv = useMemo(
    () => conversations.find((c) => c.conversation_id === activeConversation) ?? null,
    [conversations, activeConversation],
  );

  // 대화 멤버 이름 맵 (백엔드에서 조인된 name/email 사용)
  const memberNames = useMemo(() => {
    if (!activeConv?.members) return {};
    const map: Record<string, string> = {};
    for (const m of activeConv.members) {
      if (m.name) {
        map[m.user_id] = m.name;
      } else if (m.email) {
        map[m.user_id] = m.email.split('@')[0];
      } else {
        map[m.user_id] = m.user_id.includes('@')
          ? m.user_id.split('@')[0]
          : m.user_id.slice(0, 8);
      }
    }
    return map;
  }, [activeConv]);

  const memberPictures = useMemo(() => {
    if (!activeConv?.members) return {};
    const map: Record<string, string | null> = {};
    for (const m of activeConv.members) {
      map[m.user_id] = m.picture ?? null;
    }
    return map;
  }, [activeConv]);

  const handleSelectConversation = useCallback((_conv: Conversation) => {
    // activeConversation은 store에서 이미 설정됨
  }, []);

  /* P11-5h: when `mode.message.systemAppChannel` is off the system_app
   * agent thread is suppressed even if a stale `activeAgent` value points
   * to it. */
  const systemAppChannelEnabled = useFeature('mode.message.systemAppChannel');

  const showAgentThread = activeAgent
    && !activeConversation
    && (activeAgent !== 'system_app' || systemAppChannelEnabled);
  const showUserThread = activeConversation && activeConv;

  return (
    <Container>
      <MobileOverlay $open={pageSidebarOpen} onClick={togglePageSidebar} />

      {/* 왼쪽: 에이전트 + 팀 연락처 목록 */}
      <ListPane $open={pageSidebarOpen}>
        <ListPaneInner $open={pageSidebarOpen}>
          <DMAgentList
            onSettingsClick={() => setSettingsOpen(true)}
            onSelectConversation={handleSelectConversation}
            activeConversationId={activeConversation}
            pageSidebarOpen={pageSidebarOpen}
            onTogglePageSidebar={togglePageSidebar}
          />
        </ListPaneInner>
      </ListPane>

      {/* 오른쪽: 채팅 스레드 */}
      <ThreadPane>
        {!pageSidebarOpen && (
          <CollapsedSidebarToggleSlot>
            <CollapsedSidebarToggleBtn
              type="button"
              onClick={togglePageSidebar}
              title={t('dm.expandPageSidebar')}
            >
              <Menu size={16} />
            </CollapsedSidebarToggleBtn>
          </CollapsedSidebarToggleSlot>
        )}

        {showAgentThread ? (
          <DMChatThread agentType={activeAgent} />
        ) : showUserThread ? (
          <DMUserChatThread
            conversation={activeConv}
            memberNames={memberNames}
            memberPictures={memberPictures}
            enableDropZone
          />
        ) : (
          <EmptyThread>
            <EmptyIcon>
              <MessageCircle size={40} strokeWidth={1.2} />
            </EmptyIcon>
            <EmptyTitle>{t('dm.directMessage')}</EmptyTitle>
            <EmptyDesc>
              {t('dm.selectAgentOrMember')}
            </EmptyDesc>
          </EmptyThread>
        )}
      </ThreadPane>

      {/* 설정 모달 */}
      <DMSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </Container>
  );
};

export default DMPage;

/* ---- Styled ---- */

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: transparent;
`;

const MobileOverlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: ${({ theme }) => theme.colors.bgOverlay};
    z-index: 30;
  }
`;

const ListPane = styled.aside<{ $open: boolean }>`
  width: ${({ $open, theme }) => ($open ? theme.dm.listWidth : '0')};
  height: 100%;
  min-height: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    width: 280px;
    z-index: 31;
    transition: transform ${({ theme }) => theme.transitions.normal};
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  }
`;

const ListPaneInner = styled.div<{ $open: boolean }>`
  min-width: ${({ theme }) => theme.dm.listWidth};
  width: ${({ theme }) => theme.dm.listWidth};
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 280px;
    width: 280px;
    opacity: 1;
    pointer-events: auto;
  }
`;

const ThreadPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CollapsedSidebarToggleSlot = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CollapsedSidebarToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const EmptyThread = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.4;
`;

const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.dm.emptyTitleFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.emptyTitleFontSize};
  }
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.dm.emptyDescFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.dmMobile.emptyDescFontSize};
  }
`;
